import { useEffect, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CodeIcon from '@mui/icons-material/Code';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { alpha, Menu, MenuItem, Stack, styled } from '@mui/material';
import BulletList from '@tiptap/extension-bullet-list';
import CodeBlock from '@tiptap/extension-code-block';
import Heading, { Level } from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import palette from '../theme/palette';

type FontSize = {
  label: string;
  size: Level;
};

const fontSizes: FontSize[] = [
  { label: 'Normal', size: 3 },
  { label: 'Large', size: 2 },
  { label: 'Huge', size: 1 }
];

export const ToolbarButton = styled(Stack)(({ selected }: { selected: boolean | undefined }) => ({
  cursor: 'pointer',
  flexDirection: 'row',
  opacity: selected ? 1 : 0.6,
  padding: '5px',
  borderRadius: '10px',
  backgroundColor: selected ? alpha(palette.grey[600], 0.3) : 'transparent',
  ':hover': { backgroundColor: alpha(palette.grey[600], 0.6) }
}));

interface EditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const Editor = ({
  value,
  onChange,
  placeholder = 'Write here...',
  minHeight = '150px'
}: EditorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedSize, setSelectedSize] = useState<FontSize>(fontSizes[0]);

  const handleClose = (size?: FontSize) => {
    if (size) {
      if (size.size === 3) {
        editor?.chain().focus().toggleHeading({ level: selectedSize.size }).run();
      }
      setSelectedSize(size);
    }
    setAnchorEl(null);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({ levels: [1, 2] }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https']
      }),
      BulletList,
      Underline,
      OrderedList,
      CodeBlock,
      ListItem
    ],
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    }
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value.replace(/\n/g, '<br>'));
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.chain().focus().toggleHeading({ level: selectedSize.size }).run();
  }, [selectedSize]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <Stack
      sx={{
        borderRadius: '10px',
        border: `1px solid ${palette.stroke.idle}`,
        '.tiptap p': {
          margin: '10px 0px',
          fontSize: '12px'
        },
        '.tiptap a': {
          color: palette.typography.blue,
          '&:hover': {
            cursor: 'pointer'
          }
        },
        '.tiptap.ProseMirror.ProseMirror-focused': {
          border: 'none !important',
          outline: 'none !important'
        }
      }}
    >
      <Stack
        sx={{
          borderRadius: '2px',
          height: '36px',
          borderBottom: `1px solid ${palette.stroke.idle}`,
          padding: '8px'
        }}
        direction="row"
        alignItems="center"
        gap="4px"
      >
        <ToolbarButton selected={undefined} onClick={event => setAnchorEl(event.currentTarget)}>
          <FormatSizeIcon sx={{ width: '20px', height: '20px' }} />
          <ArrowDropDownIcon sx={{ width: '18px', height: '18px' }} />
        </ToolbarButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()}>
          {fontSizes.map(size => (
            <MenuItem
              key={size.label}
              onClick={() => handleClose(size)}
              style={{ fontSize: 45 - size.size * 10 }}
            >
              <Stack width="25px">
                {selectedSize.label === size.label ? (
                  <CheckIcon sx={{ width: '18px', height: '18px' }} />
                ) : (
                  ''
                )}
              </Stack>
              {size.label}
            </MenuItem>
          ))}
        </Menu>

        <ToolbarButton
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon sx={{ width: '20px', height: '20px' }} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          selected={editor.isActive('italic')}
        >
          <FormatItalicIcon sx={{ width: '18px', height: '18px' }} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          selected={editor.isActive('underline')}
        >
          <FormatUnderlinedIcon sx={{ width: '18px', height: '18px' }} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          selected={editor.isActive('codeBlock', { level: 2 })}
        >
          <CodeIcon sx={{ width: '18px', height: '18px' }} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          selected={editor.isActive('bulletList')}
        >
          <FormatListBulletedIcon sx={{ width: '20px', height: '20px' }} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          selected={editor.isActive('orderedList')}
        >
          <FormatListNumberedIcon sx={{ width: '20px', height: '20px' }} />
        </ToolbarButton>
      </Stack>

      <Stack padding="12px">
        <EditorContent editor={editor} placeholder={placeholder} style={{ minHeight: minHeight }} />
      </Stack>
    </Stack>
  );
};

export default Editor;
